import { useCallback, useEffect } from 'react';

import { jwtDecode, useAdvisorDetailsStore, useUserDetailsStore } from '@zeta-core';

import { AdvisorAPIs, QueryAPIs, UserServiceAPIs } from '@zeta-services';
import { useNavigate } from 'react-router';
import { RouterLinks } from '../app-route-paths';
import { isValidToken, setSession, STORAGE_KEY } from './jwt';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthGuard({ children }: Props) {
  const navigate = useNavigate();
  const updateUser = useUserDetailsStore((state) => state.updateUser);
  const updatePlanningStatus = useUserDetailsStore((state) => state.updatePlanningStatus);
  const updateAdvisor = useAdvisorDetailsStore((state) => state.updateAdvisor);
  const updateDashboard = useAdvisorDetailsStore((state) => state.updateDashboard);

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const decodedToken = jwtDecode(accessToken);
        if (decodedToken?.username) {
          const res = await UserServiceAPIs().userDetails({ userId: decodedToken?.username ?? '' });
          updateUser(res.data?.responseItems?.[0]);
          const advisorResponse = await QueryAPIs().viewClientDashboard({});
          updateDashboard(advisorResponse?.data);
          updateAdvisor(advisorResponse?.data?.advisor);
          const planningStatus = await AdvisorAPIs().viewClientEngagementDetails({ caseID: advisorResponse?.data?.caseID });
          updatePlanningStatus(planningStatus?.data?.response[0]?.planningStatus);
        }
      } else {
        navigate(RouterLinks.login);
      }
    } catch (error) {
      console.error(error);
    }
  }, [navigate, updateUser]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
}
